import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { useSelector } from "react-redux";

import { selectMembers } from "../../../../core/data/redux/projects/selectors";
import api from "../../../../services/api";
import UserAvatar from "../../components/UserAvatar";

import type { UserSnapshot } from "../../../../core/data/redux/projects/types";

interface AssigneeOption {
  value: number;
  label: string;
  email: string;
  name: string;
  uid: string | null;
  img: string | null;
}

interface AssigneePickerProps {
  value: number | null | undefined;
  assigneeSnapshot?: UserSnapshot | null;
  onChange: (
    userId: number | null,
    snapshot: Record<string, any> | null
  ) => void;
  disabled?: boolean;
  size?: "small" | "middle" | "large";
}

const fetchEmployees = async (): Promise<AssigneeOption[]> => {
  const signature = localStorage.getItem("signature");
  const response = await api.post(
    "userService/getActiveEmployeesListData",
    { signature }
  );
  const payload = response.data?.data?.data || response.data?.data || {};
  const list = Array.isArray(payload) ? payload : payload.allUsers || [];
  if (!Array.isArray(list)) return [];
  return list
    .filter((emp: any) => emp.id ?? emp.user_id ?? emp.key)
    .map((emp: any) => {
      const id = emp.id ?? emp.user_id ?? emp.key;
      const name =
        emp.Name ||
        `${emp.Fname || emp.fname || ""} ${emp.Lname || emp.lname || ""}`.trim() ||
        emp.Email || emp.email ||
        `User #${id}`;
      const email = emp.Email || emp.email || "";
      return {
        value: Number(id),
        label: name,
        email,
        name,
        uid: emp.UID || emp.uid || emp.EmpId || null,
        img: emp.Image || emp.profile_pic || null,
      };
    });
};

let cachedEmployees: AssigneeOption[] | null = null;
let cachePromise: Promise<AssigneeOption[]> | null = null;

const getEmployees = async (): Promise<AssigneeOption[]> => {
  if (cachedEmployees) return cachedEmployees;
  if (!cachePromise) {
    cachePromise = fetchEmployees()
      .then((list) => {
        cachedEmployees = list;
        return list;
      })
      .catch(() => {
        cachePromise = null;
        return [];
      });
  }
  return cachePromise;
};

const renderRow = (opt: AssigneeOption) => (
  <span className="d-inline-flex align-items-center gap-2">
    <UserAvatar name={opt.name} img={opt.img} size={22} fontSize={10} />
    <span className="d-inline-flex flex-column" style={{ lineHeight: 1.15 }}>
      <span style={{ fontSize: 13 }}>{opt.name}</span>
      {opt.email && (
        <span className="text-muted" style={{ fontSize: 11 }}>
          {opt.email}
        </span>
      )}
    </span>
  </span>
);

const AssigneePicker: React.FC<AssigneePickerProps> = ({
  value,
  assigneeSnapshot,
  onChange,
  disabled,
  size = "middle",
}) => {
  const members = useSelector(selectMembers);
  const [allEmployees, setAllEmployees] = useState<AssigneeOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getEmployees()
      .then((list) => {
        if (mounted) setAllEmployees(list);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const options = useMemo<AssigneeOption[]>(() => {
    const memberIds = new Set(members.map((m) => Number(m.user_id)));

    const fromMembers: AssigneeOption[] = members.map((m) => {
      const empMatch = allEmployees.find((e) => e.value === Number(m.user_id));
      const name = m.user_snapshot?.name || empMatch?.name || `User #${m.user_id}`;
      const email = m.user_snapshot?.email || empMatch?.email || "";
      return {
        value: Number(m.user_id),
        label: name,
        email,
        name,
        uid: (m.user_snapshot as any)?.uid || empMatch?.uid || null,
        img: m.user_snapshot?.img || empMatch?.img || null,
      };
    });

    if (value != null) {
      const numVal = Number(value);
      if (!memberIds.has(numVal)) {
        const stray = allEmployees.find((e) => e.value === numVal);
        if (stray) {
          fromMembers.push({ ...stray, name: `${stray.name} (not a member)` });
        } else if (assigneeSnapshot) {
          fromMembers.push({
            value: numVal,
            label: assigneeSnapshot.name || `User #${numVal}`,
            name: assigneeSnapshot.name || `User #${numVal}`,
            email: assigneeSnapshot.email || "",
            uid: assigneeSnapshot.uid || null,
            img: assigneeSnapshot.img || null,
          });
        } else {
          fromMembers.push({
            value: numVal,
            label: `User #${numVal}`,
            name: `User #${numVal}`,
            email: "",
            uid: null,
            img: null,
          });
        }
      }
    }

    return fromMembers;
  }, [members, allEmployees, value, assigneeSnapshot]);

  const selectOptions = useMemo(
    () =>
      options.map((o) => ({
        value: o.value,
        label: renderRow(o),
        searchText: `${o.name} ${o.email}`.toLowerCase(),
      })),
    [options]
  );

  const handleChange = (v: number | null | undefined) => {
    if (v == null) {
      onChange(null, null);
      return;
    }
    const numV = Number(v);
    const found = options.find((o) => o.value === numV);
    if (!found) {
      onChange(numV, null);
      return;
    }
    onChange(numV, {
      id: found.value,
      name: found.name,
      email: found.email,
      uid: found.uid,
      img: found.img,
    });
  };

  const numericValue = value != null ? Number(value) : undefined;

  return (
    <Select
      style={{ width: "100%", height: 36 }}
      showSearch
      allowClear
      placeholder={members.length === 0 ? "Add project members first" : "Unassigned"}
      value={numericValue}
      onChange={handleChange}
      disabled={disabled}
      size={size}
      loading={loading}
      options={selectOptions}
      optionLabelProp="label"
      notFoundContent={members.length === 0 ? "No project members yet" : "No match"}
      filterOption={(input, option) =>
        String((option as any)?.searchText || "")
          .includes(input.toLowerCase())
      }
    />
  );
};

export default AssigneePicker;
